import React, {FC, useCallback, useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {getAvailableAssignees, reassignTask} from '../../../../../../api/tasks';
import ReassignForm from './ReassignForm';
import Popup from '../../../../../common/popup/Popup';
import {Button} from '../../../../../common/Button';
import Spinner from '../../../../../common/Spinner';
import {listPopupStyles} from '../../../../../../constants/popupStyles';
import routes from '../../../../routes';
import {AvailableAssignee} from '../../../../types';
import {useSnackbarHook} from '../../../../../../hooks/useSnackbar';

type ReassignPopupProps = {
  taskId: number,
  isCurrentUserAssignee: boolean,
  isRunning: boolean
}

const ReassignPopup: FC<ReassignPopupProps> = ({
  taskId, isCurrentUserAssignee, isRunning
}) => {
  const {t} = useTranslation('tasks');
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [assignees, setAssignees] = useState<AvailableAssignee[]>([]);
  const {addSuccessToast, addErrorToast} = useSnackbarHook();

  const handleReassignTask = useCallback(async ({assigneeId}) => {
    setIsLoading(true);

    try {
      await reassignTask(taskId, assigneeId);
      history.push(routes.tasks());
      addSuccessToast(t('reassign.toastMessage'));
    } catch (e) {
      addErrorToast(e?.message);
    } finally {
      setIsLoading(false);
    }
  }, [taskId]);

  const togglePopupVisibility = useCallback(() => {
    setIsOpen((prevState) => !prevState);
  }, []);

  const getAssignees = useCallback(async () => {
    setIsLoading(true);
    try {
      const receivedAssignees = await getAvailableAssignees(taskId);
      setAssignees(receivedAssignees);
    } catch (e) {
      addErrorToast(e?.message);
    } finally {
      setIsLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    if (isRunning && isCurrentUserAssignee) {
      getAssignees();
    }
  }, [isCurrentUserAssignee, getAssignees, isRunning]);

  const isDisabled =
    !isRunning ||
    !isCurrentUserAssignee ||
    isLoading ||
    !assignees.length;

  return (
    <>
      <Button
        label={t('reassign.label')}
        variant="text"
        onClick={togglePopupVisibility}
        disabled={isDisabled}
      />

      {isCurrentUserAssignee && (
        <Popup
          isOpen={isOpen}
          closeModal={togglePopupVisibility}
          shouldCloseOnOverlayClick={!isLoading}
          contentLabel={t('reassign.popup.contentLabel')}
          popupStyles={listPopupStyles}
        >
          <Spinner isLoading={isLoading}>
            <div className="list-popup">
              <h4 className="popup-title">{t('reassign.popup.title')}</h4>
              <ReassignForm
                assignees={assignees}
                togglePopupVisibility={togglePopupVisibility}
                onSubmit={handleReassignTask}
              />
            </div>
          </Spinner>
        </Popup>
      )}
    </>
  );
};

export default ReassignPopup;
